import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import BackButton from "../../common/back-button";
import HeightButton from "../../common/height-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { CAMPAIGN_LIST_ENDPOINT, CAMPAIGN_OVERVIEW_ENDPOINT, GET_RCS_BOTS, getCampaignBroadcastsEndpoint } from "../../config/urls";
import { ChannelBadge, StatusBadge } from "./campaignBadges";
import BroadcastTemplates from "../BroadcastTemplates/BroadcasteTemplates";
import RCSBroadcastTemplate from "../Rcs/RCSBroadcastTemplate";

const CAMPAIGN_NAME_MAX = 50;
const NAME_MAX = 100;
const DESCRIPTION_MAX = 250;

const cardSx = { bgcolor: "#fff", border: "1px solid #E3E5EA", borderRadius: "12px", p: 2.5, mb: 2 };

// Same channel markers as the WhatsApp/RCS page tabs
const getChannelAccess = () => {
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasRcs = permissions.includes("rcs_bot_create.view");
  return { hasRcs, hasWa: permissions.includes("conversations.view") || !hasRcs };
};

const ChannelCard = ({ label, selected, disabled, disabledLabel = "Not enabled", onClick }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      border: "2px solid",
      borderColor: selected ? "#14204D" : "#E3E5EA",
      bgcolor: disabled ? "#F6F7F9" : selected ? "#EEF1FA" : "#fff",
      borderRadius: "12px",
      p: 2,
      textAlign: "center",
      cursor: disabled ? "default" : "pointer",
      userSelect: "none",
    }}
  >
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color: disabled ? "#9AA0AC" : selected ? "#14204D" : "#3B4250" }}>
      {label}
    </Typography>
    <Typography sx={{ ...typography.bodySmall, color: disabled ? "#9AA0AC" : selected ? "#14204D" : "#9AA0AC" }}>
      {disabled ? disabledLabel : selected ? "Selected" : "Available"}
    </Typography>
  </Box>
);

// Step before the WhatsApp/RCS broadcast forms: set up the campaign, name the broadcast and pick the channel.
// mode "create" starts a new campaign with this broadcast; mode "add" attaches it to an existing campaign;
// mode "edit" reopens a scheduled WhatsApp broadcast (pageParams.edit) with campaign, channel and name locked.
const CampaignSetup = (props) => {
  const { getValidToken } = useAuth();
  const { hasWa, hasRcs } = useMemo(getChannelAccess, []);
  const mode = props?.pageParams?.mode || "add";
  const isCreate = mode === "create";
  const isQuick = mode === "quick"; // single contact: no campaign to pick, name is set automatically
  const isEdit = mode === "edit";
  const editParams = props?.pageParams?.edit;
  // Opened from a campaign row ("+ Add broadcast") or editing: the campaign is fixed
  const lockCampaign = isEdit || !!props?.pageParams?.campaignId;
  // Opened from the WhatsApp template list: that template is preselected
  const presetTemplate = props?.pageParams?.template || null;

  const [campaignName, setCampaignName] = useState("");
  const [campaignDescription, setCampaignDescription] = useState("");
  const [existingNames, setExistingNames] = useState([]);

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignBroadcasts, setCampaignBroadcasts] = useState([]);
  const [broadcastName, setBroadcastName] = useState(isEdit ? editParams?.broadcastName || "" : "");
  const [channel, setChannel] = useState(isEdit || hasWa ? "whatsapp" : "rcs");
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState("");
  // Fields the user has touched (or all of them after a submit attempt): only their errors are shown
  const [touched, setTouched] = useState({});
  const touch = (field) => setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  const authHeaders = async () => ({
    Authorization: `Bearer ${await getValidToken()}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": localStorage.getItem("tenant"),
  });

  useEffect(() => {
    if (!isCreate) return;
    // Same duplicate-name check as the Broadcast page's create modal
    const fetchCampaignNames = async () => {
      try {
        const response = await fetch(`${CAMPAIGN_LIST_ENDPOINT}?limit=1000`, { headers: await authHeaders() });
        const data = await response.json();
        setExistingNames((data.results || []).map((c) => (c.campaign_name || "").toLowerCase()));
      } catch (error) {
        console.error(error);
      }
    };
    fetchCampaignNames();
  }, [isCreate]);

  useEffect(() => {
    if (isCreate || isQuick) return;
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`${CAMPAIGN_OVERVIEW_ENDPOINT}?limit=500`, { headers: await authHeaders() });
        const data = await response.json();
        const results = data.results || [];
        setCampaigns(results);
        const preselectedId = props?.pageParams?.campaignId;
        if (preselectedId) setSelectedCampaign(results.find((c) => c.id === preselectedId) || null);
      } catch (error) {
        console.error(error);
      }
    };
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (!selectedCampaign) {
      setCampaignBroadcasts([]);
      return;
    }
    const fetchBroadcasts = async () => {
      try {
        const response = await fetch(getCampaignBroadcastsEndpoint(selectedCampaign.id), { headers: await authHeaders() });
        const data = await response.json();
        setCampaignBroadcasts(data.results || []);
      } catch (error) {
        console.error(error);
        setCampaignBroadcasts([]);
      }
    };
    fetchBroadcasts();
  }, [selectedCampaign]);

  useEffect(() => {
    if (channel !== "rcs" || bots.length) return;
    const fetchBots = async () => {
      try {
        const response = await axios.get(GET_RCS_BOTS, { headers: { "X-Tenant-ID": localStorage.getItem("tenant") || "" } });
        const data = response.data;
        setBots(Array.isArray(data) ? data : data?.results || data?.data || []);
      } catch (error) {
        console.error(error);
        setBots([]);
      }
    };
    fetchBots();
  }, [channel]);

  // Unsaved changes: flagged on the first edit inside the page (typing, uploads, picking an option),
  // cleared once the broadcast is submitted. Leaving through this page's Back/Cancel asks first;
  // refresh/close gets the browser's own prompt. Sidebar links and browser Back are not guarded.
  const dirtyRef = useRef(false);
  const submittedRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [pendingLeave, setPendingLeave] = useState(null);
  const markDirty = () => {
    if (submittedRef.current || dirtyRef.current) return;
    dirtyRef.current = true;
    setDirty(true);
  };
  const markDirtyOnOptionPick = (e) => e.target.closest?.('[role="option"]') && markDirty();
  const onSubmitted = () => {
    submittedRef.current = true;
    dirtyRef.current = false;
    setDirty(false);
  };
  const guardedNavigate = (...args) => {
    if (dirtyRef.current && !submittedRef.current) setPendingLeave(() => () => props.onNavigate(...args));
    else props.onNavigate(...args);
  };
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const goBack = () => guardedNavigate("campaigns_v2", { tab: isQuick ? "single" : "campaigns" });

  // Checks the campaign and broadcast cards; recomputed every render so the submit button can stay disabled
  const getSetupErrors = () => {
    if (isEdit) return {}; // nothing on these cards can change while editing
    const nextErrors = {};
    if (isQuick) {
      // nothing to pick: the send gets its own hidden "Single Contact" campaign
    } else if (isCreate) {
      if (!campaignName.trim()) nextErrors.campaignName = "Campaign name is required";
      else if (existingNames.includes(campaignName.trim().toLowerCase())) nextErrors.campaignName = "Campaign name already exists";
    } else if (!selectedCampaign) {
      nextErrors.campaign = "Select a campaign";
    }
    if (!isQuick && !broadcastName.trim()) nextErrors.broadcastName = "Broadcast name is required";
    // Unique within the campaign, across WhatsApp and RCS (the backend checks the same)
    else if (
      !isQuick && !isCreate && !isEdit &&
      campaignBroadcasts.some((b) => (b.name || "").trim().toLowerCase() === broadcastName.trim().toLowerCase())
    ) {
      nextErrors.broadcastName = "A broadcast with this name already exists. Please enter a unique broadcast name.";
    }
    if (channel === "rcs" && !selectedBotId) nextErrors.bot = "Select an RCS bot";
    return nextErrors;
  };
  const setupErrors = getSetupErrors();
  const setupValid = Object.keys(setupErrors).length === 0;
  const errors = Object.fromEntries(Object.entries(setupErrors).filter(([field]) => touched[field]));

  // Used by the inline forms' submit: reveals every setup error
  const validateSetup = () => {
    setTouched({ campaignName: true, campaign: true, broadcastName: true, bot: true });
    return setupValid;
  };

  const buildParams = () => {

    const params = isQuick
      ? {
          campaignName: "Single Contact",
          campaignDescription: "",
          campaignType: "quick",
          lastPage: "campaigns_v2",
          returnTab: "single",
          broadcastName: `Quick broadcast · ${dayjs().format("D MMM, h:mm A")}`,
        }
      : {
          campaignName: isCreate ? campaignName.trim() : selectedCampaign?.campaign_name || "",
          campaignDescription: isCreate ? campaignDescription.trim() : selectedCampaign?.campaign_description || "",
          campaignType: "standard",
          lastPage: "campaigns_v2",
          returnTab: "campaigns",
          existingCampaignId: isCreate ? undefined : selectedCampaign?.id,
          broadcastName: broadcastName.trim(),
        };
    return params;
  };

  const inlineWhatsApp = channel === "whatsapp";
  const inlineRcs = channel === "rcs";
  const selectedBot = bots.find((bot) => bot.id === selectedBotId);
  const singlePageConfig = {
    ...buildParams(),
    validateSetup,
    setupValid,
    onSubmitted,
    quick: isQuick,
    submitLabel: isEdit ? "Save changes" : isQuick ? "Send now" : isCreate ? "Create campaign" : "Add broadcast",
  };
  const formPageParams = isEdit
    ? { ...editParams, campaignType: "standard" }
    : { campaignType: isQuick ? "quick" : "standard", ...(presetTemplate && { fromTemplatesTable: true, ...presetTemplate }) };

  return (
    <Container maxWidth={false} sx={{ bgcolor: "#FAFAFA", pt: "17px", pb: 4 }}>
      <Box onChangeCapture={markDirty} onClickCapture={markDirtyOnOptionPick}>
        <Box sx={{ mb: 2 }}>
          <BackButton onClick={goBack} />
        </Box>
        <Typography sx={{ ...typography.cardTitle, fontWeight: 700 }}>
          {isEdit ? "Edit scheduled broadcast" : isQuick ? "Single/Quick broadcast" : isCreate ? "Create campaign" : "Add broadcast to campaign"}
        </Typography>
        <Typography sx={{ ...typography.bodySmall, color: "#6B7280", mb: 2 }}>
          {isEdit
            ? "Change the template, the send time or the audience list of this broadcast before it goes out."
            : isQuick
            ? "Send one message to one contact right now. No campaign needed."
            : isCreate
            ? "Set up the campaign and its first broadcast."
            : "Attach a new broadcast to an existing campaign. It keeps the campaign's name and analytics."}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: isQuick ? "1fr" : "1fr 1fr" }, gap: 2, alignItems: "stretch", mb: 2 }}>
        {isCreate && (
          <Box sx={{ ...cardSx, mb: 0 }}>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, mb: 1.5 }}>Campaign details</Typography>
            <TextField
              fullWidth
              label="Campaign name *"
              value={campaignName}
              onChange={(e) => {
                setCampaignName(e.target.value.slice(0, CAMPAIGN_NAME_MAX));
                touch("campaignName");
              }}
              error={!!errors.campaignName}
              helperText={errors.campaignName || `${campaignName.length}/${CAMPAIGN_NAME_MAX}`}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Campaign description"
              value={campaignDescription}
              onChange={(e) => setCampaignDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
              helperText={`${campaignDescription.length}/${DESCRIPTION_MAX}`}
            />
          </Box>
        )}

        {!isCreate && !isQuick && (
        <Box sx={{ ...cardSx, mb: 0 }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, mb: 1.5 }}>Campaign</Typography>
          <Autocomplete
            options={campaigns}
            value={selectedCampaign}
            disabled={lockCampaign}
            disableClearable={lockCampaign}
            onChange={(event, value) => {
              setSelectedCampaign(value);
              touch("campaign");
            }}
            getOptionLabel={(option) => option.campaign_name || `Campaign ${option.id}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(optionProps, option) => (
              <li {...optionProps} key={option.id}>
                {option.campaign_name}
                <Typography component="span" sx={{ ...typography.bodySmall, color: "#9AA0AC", ml: 1 }}>
                  {option.status === "active" ? "Active" : "Completed"} · {option.broadcast_count} broadcast{option.broadcast_count === 1 ? "" : "s"}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select campaign *" error={!!errors.campaign} helperText={errors.campaign} />
            )}
          />

          {selectedCampaign && (
            <Box sx={{ mt: 1.5, border: "1px solid #E3E5EA", borderRadius: "10px", p: 1.75, bgcolor: "#FAFBFC" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <StatusBadge status={selectedCampaign.status} />
                <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
                  {selectedCampaign.start_date ? dayjs(selectedCampaign.start_date).format("MMM D, YYYY") : ""}
                  {selectedCampaign.end_date && selectedCampaign.end_date !== selectedCampaign.start_date
                    ? ` – ${dayjs(selectedCampaign.end_date).format("MMM D, YYYY")}`
                    : ""}
                  {selectedCampaign.created_by ? ` · created by ${selectedCampaign.created_by}` : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                {selectedCampaign.channels.map((c) => (
                  <ChannelBadge key={c} channel={c} />
                ))}
                <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
                  {campaignBroadcasts.length} broadcast{campaignBroadcasts.length === 1 ? "" : "s"} so far
                </Typography>
              </Box>
              {campaignBroadcasts.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                  {campaignBroadcasts.map((b) => (
                    <Box key={`${b.channel}-${b.id}`} sx={{ ...typography.bodySmall, bgcolor: "#EDEFF3", borderRadius: "999px", px: 1.25, py: 0.25 }}>
                      {b.name}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
        )}

        <Box sx={{ ...cardSx, mb: 0 }}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, mb: 1.5 }}>{isQuick ? "Channel" : "Broadcast setup"}</Typography>

          <Typography sx={{ ...typography.bodySmall, fontWeight: 600, mb: 1 }}>Channel *</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 1.25, mb: 2 }}>
            <ChannelCard label="WhatsApp" selected={channel === "whatsapp"} disabled={!hasWa} onClick={() => !isEdit && setChannel("whatsapp")} />
            <ChannelCard label="RCS" selected={channel === "rcs"} disabled={!hasRcs || isEdit || !!presetTemplate} disabledLabel={isEdit || presetTemplate ? "Locked" : "Not enabled"} onClick={() => setChannel("rcs")} />
            <ChannelCard label="Email" disabled disabledLabel="Coming soon" />
            <ChannelCard label="SMS" disabled disabledLabel="Coming soon" />
          </Box>

          {channel === "rcs" && (
            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.bot}>
              <InputLabel>Select RCS bot *</InputLabel>
              <Select value={selectedBotId} label="Select RCS bot *" onChange={(e) => {
                setSelectedBotId(e.target.value);
                touch("bot");
              }}>
                {bots.map((bot) => (
                  <MenuItem key={bot.id} value={bot.id}>
                    {bot.bot_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.bot && <Typography sx={{ ...typography.bodySmall, color: "#B3261E", mt: 0.5 }}>{errors.bot}</Typography>}
            </FormControl>
          )}

          {isQuick && (
            <Typography sx={{ ...typography.bodySmall, color: "#6B7280", bgcolor: "#F6F7F9", borderRadius: "8px", px: 1.5, py: 1.1 }}>
              Broadcast name: {buildParams().broadcastName} (set automatically)
            </Typography>
          )}
          {!isQuick && (
            <>
          <TextField
            fullWidth
            label="Broadcast name *"
            value={broadcastName}
            disabled={isEdit}
            onChange={(e) => {
              setBroadcastName(e.target.value.slice(0, NAME_MAX));
              touch("broadcastName");
            }}
            error={!!errors.broadcastName}
            helperText={errors.broadcastName || `${broadcastName.length}/${NAME_MAX}`}
          />
            </>
          )}
        </Box>
        </Box>

        {inlineWhatsApp ? (
          <BroadcastTemplates {...props} onNavigate={guardedNavigate} pageParams={formPageParams} singlePage={singlePageConfig} />
        ) : inlineRcs ? (
          selectedBot ? (
            // Remount per bot: templates are loaded for the selected bot
            <RCSBroadcastTemplate
              key={selectedBot.id}
              bot={selectedBot}
              onBack={goBack}
              props={{ ...props, onNavigate: guardedNavigate, pageParams: formPageParams }}
              singlePage={singlePageConfig}
            />
          ) : (
            <Typography sx={{ ...typography.bodySmall, color: "#6B7280", textAlign: "center", py: 3 }}>
              Select an RCS bot to choose a template.
            </Typography>
          )
        ) : null}
      </Box>

      <Dialog open={!!pendingLeave} onClose={() => setPendingLeave(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...typography.cardTitle, fontWeight: 700 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...typography.bodyLarge }}>Any information entered will be lost.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <HeightButton variant="outlined" title="Keep editing" onClick={() => setPendingLeave(null)} />
          <HeightButton
            variant="contained"
            title="Discard & leave"
            onClick={() => {
              const leave = pendingLeave;
              dirtyRef.current = false;
              setDirty(false);
              setPendingLeave(null);
              leave();
            }}
          />
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CampaignSetup;
